import { gt } from 'common/PatternMatchingUtilities';
import { isDefined } from 'common/TypeUtilities';
import React, { useCallback, useMemo } from 'react';
import { useRouteMatch } from 'react-router';
import { getMatchingDishesFiltersCount } from 'src/pages/consumer/dish-first/explore/utils';
import { useDishFirstExploreUrl } from 'src/pages/consumer/explore/useExploreUrl';
import { getShefAvailabilityDateString } from 'src/shared/design-system/DishFirstShefCard/utils';
import { match as tsMatch } from 'ts-pattern';
import { useScreenContext } from 'web-common/src/shared/context/ScreenContext/hook';
import { ScreenWidth, ScreenWidthValues } from 'web-common/src/shared/styles';
import { ShefCard, ShefCardHalfLandscape, ShefCardLandscape } from './styles';
import { ExploreShefCardProps } from './types';

const MAX_DELIVERY_PRICE_TO_RENDER = 10000; // Dont show anything over $100 on the shef's menu - e.g. if I view a portland shef while im in the bay
export const ExploreShefCard: React.FC<ExploreShefCardProps> = (props) => {
  const {
    className,
    idx,
    deliveryDate,
    exploreSegmentShef,
    segment,
    segmentMetrics,
    scrollPosition,
    onClick,
    href,
    location,
  } = props;

  const {
    windowDimensions: { width },
  } = useScreenContext();

  const handleClick = useCallback(() => {
    onClick({ exploreSegmentShef, segmentMetrics, idx });
  }, [onClick, exploreSegmentShef, segmentMetrics, idx]);

  const { shef } = exploreSegmentShef;
  const nextDeliveryDate = useMemo(
    () => getShefAvailabilityDateString(shef.availability, location, deliveryDate),
    [shef.availability, deliveryDate, location]
  );

  const match = useRouteMatch();
  const filters = useDishFirstExploreUrl({ match });
  const showDeliveryPrice =
    isDefined(shef.deliveryPrice) && shef.deliveryPrice.finalDeliveryPrice < MAX_DELIVERY_PRICE_TO_RENDER;
  // Dish count is probably less relevant for shef first markets given we dont have as much choie paralysis, so we use the display slot for
  // delivery price instead
  const showDishCount = !showDeliveryPrice && getMatchingDishesFiltersCount(filters) > 0;

  const cardProps = {
    className,
    deliveryDate,
    exploreSegmentShef,
    segment,
    scrollPosition,
    onClick: handleClick,
    href,
    showDishCount,
    showDeliveryPrice,
    nextDeliveryDate,
  };

  return (
    tsMatch({ isResponsive: props.isResponsive, width })
      .with({ isResponsive: false }, () => <ShefCard {...cardProps} />)
      // Full screen uses portrait cards
      .with({ width: gt(ScreenWidthValues[ScreenWidth.MEDIUM]) }, () => <ShefCard {...cardProps} />)
      // Intermediate type for 2 columns of landscape cards
      .with({ width: gt(ScreenWidthValues[ScreenWidth.SMALL]) }, () => <ShefCardHalfLandscape {...cardProps} />)
      // Landscape card for full width of mobile screens
      .otherwise(() => <ShefCardLandscape {...cardProps} />)
  );
};
